import type { ReactNode } from "react";
import { AlertTriangle, CheckCircle2, Circle, ShieldCheck } from "lucide-react";
import { LoadingButton } from "@/components/loading-button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { ComposeTextarea } from "@/components/compose-field";

export type ReviewCheckState = "complete" | "warning" | "blocking" | "pending";

export type ReviewCheck = {
  id: string;
  label: string;
  detail?: string;
  state: ReviewCheckState;
  count?: number;
  actionLabel?: string;
  onClick?: () => void;
};

function CheckIcon({ state }: { state: ReviewCheckState }) {
  if (state === "complete")
    return <CheckCircle2 className="size-4 text-emerald-600" />;
  if (state === "blocking")
    return <AlertTriangle className="size-4 text-destructive" />;
  if (state === "warning")
    return <AlertTriangle className="size-4 text-amber-600" />;
  return <Circle className="size-4 text-muted-foreground" />;
}

const CHECK_SECTIONS: Array<{
  id: string;
  title: string;
  states: ReviewCheckState[];
}> = [
  {
    id: "needs-action",
    title: "Needs action",
    states: ["blocking", "pending"],
  },
  { id: "advisories", title: "Advisories", states: ["warning"] },
  { id: "ready", title: "Ready", states: ["complete"] },
];

/**
 * Shared sticky review rail for quote, PO, and approval surfaces. A total can
 * render its source editor when the workflow owns an explicit adjustment.
 */
export function ReviewApprovalPanel({
  title = "Review and approval",
  titleId = "review-approval-title",
  checks,
  totals,
  blockingCount,
  showBlockingCount = true,
  showCheckSections = true,
  note,
  notePlaceholder = "Approval note (optional)",
  totalsNote = "Totals are calculated from included line items and cannot be edited here.",
  primaryLabel,
  primaryDisabled = false,
  primaryPending = false,
  primaryHint,
  footer,
  className,
  onNoteChange,
  onPrimaryAction,
  wrapPrimary = (button) => button,
}: {
  title?: string;
  titleId?: string;
  checks: ReviewCheck[];
  totals: Array<{ label: string; value: ReactNode; emphasized?: boolean }>;
  blockingCount: number;
  showBlockingCount?: boolean;
  showCheckSections?: boolean;
  note?: string;
  notePlaceholder?: string;
  totalsNote?: string;
  primaryLabel: string;
  primaryDisabled?: boolean;
  primaryPending?: boolean;
  primaryHint?: string;
  footer?: ReactNode;
  className?: string;
  onNoteChange?: (value: string) => void;
  onPrimaryAction: () => void;
  /** Wraps the primary button, e.g. to anchor a confirmation popover to it. */
  wrapPrimary?: (button: ReactNode) => ReactNode;
}) {
  return (
    <aside
      aria-labelledby={titleId}
      className={cn(
        "overflow-hidden rounded-xl border bg-background xl:flex xl:max-h-[calc(100vh-3rem)] xl:flex-col",
        className,
      )}
    >
      <div className="shrink-0 border-b px-4 py-4">
        <div className="min-w-0">
          <h2 id={titleId} className="flex items-center gap-2 font-semibold">
            <ShieldCheck className="size-4" aria-hidden="true" /> {title}
          </h2>
          {blockingCount && showBlockingCount ? (
            <p className="mt-1 text-sm text-muted-foreground">
              {blockingCount} blocking item{blockingCount === 1 ? "" : "s"}{" "}
              remaining
            </p>
          ) : null}
        </div>
      </div>

      <div className="divide-y xl:flex xl:min-h-0 xl:flex-1 xl:flex-col">
        <div className="px-3 py-3 xl:min-h-0 xl:flex-1 xl:overflow-y-auto">
          {CHECK_SECTIONS.map((section) => {
            const sectionChecks = checks.filter((check) =>
              section.states.includes(check.state),
            );
            if (!sectionChecks.length) return null;
            const headingId = `${titleId}-${section.id}`;
            return (
              <section
                key={section.id}
                aria-labelledby={showCheckSections ? headingId : undefined}
                aria-label={showCheckSections ? undefined : section.title}
                className="not-first:mt-4"
              >
                {showCheckSections ? (
                  <h3
                    id={headingId}
                    className="mb-1 px-2 text-xs font-semibold text-muted-foreground"
                  >
                    {section.title}
                  </h3>
                ) : null}
                <div className="space-y-1">
                  {sectionChecks.map((check) => {
                    const content = (
                      <>
                        <CheckIcon state={check.state} />
                        <span className="min-w-0 flex-1">
                          <span className="flex items-center justify-between gap-2">
                            <span className="block text-sm font-medium">
                              {check.label}
                            </span>
                            {check.count != null ? (
                              <Badge
                                variant={
                                  check.state === "blocking"
                                    ? "destructive"
                                    : "outline"
                                }
                                className="min-w-6 shrink-0 justify-center px-1.5 tabular-nums"
                                aria-label={`${check.label}: ${check.count} item${check.count === 1 ? "" : "s"}`}
                              >
                                {check.count}
                              </Badge>
                            ) : null}
                          </span>
                          {check.detail && check.state !== "complete" ? (
                            <span className="block whitespace-normal break-words text-xs text-muted-foreground">
                              {check.detail}
                            </span>
                          ) : null}
                          {check.onClick && check.actionLabel ? (
                            <span className="mt-0.5 block text-xs font-medium text-foreground">
                              {check.actionLabel}
                            </span>
                          ) : null}
                        </span>
                      </>
                    );
                    return check.onClick ? (
                      <button
                        key={check.id}
                        type="button"
                        className="flex min-h-11 w-full items-center gap-2 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
                        onClick={check.onClick}
                      >
                        {content}
                      </button>
                    ) : (
                      <div
                        key={check.id}
                        className="flex min-h-11 items-center gap-2 px-2 py-1.5"
                      >
                        {content}
                      </div>
                    );
                  })}
                </div>
              </section>
            );
          })}
        </div>

        <dl className="shrink-0 space-y-2 overflow-hidden px-4 py-4">
          {totals.map((total) => (
            <div
              key={total.label}
              className={cn(
                "grid min-h-9 grid-cols-[minmax(0,1fr)_auto] items-stretch gap-4 text-sm tabular-nums",
                total.emphasized && "border-t pt-3 text-base font-semibold",
              )}
            >
              <dt
                className={cn(
                  "flex min-w-0 items-center whitespace-nowrap",
                  !total.emphasized && "text-muted-foreground",
                )}
              >
                {total.label}
              </dt>
              <dd className="flex min-w-0 items-center justify-end text-right">
                {total.value}
              </dd>
            </div>
          ))}
          {totalsNote ? (
            <p className="pt-1 text-xs text-muted-foreground">{totalsNote}</p>
          ) : null}
        </dl>

        <div className="shrink-0 space-y-3 px-4 py-4">
          {onNoteChange ? (
            <ComposeTextarea
              value={note ?? ""}
              onChange={(event) => onNoteChange(event.target.value)}
              placeholder={notePlaceholder}
              className="min-h-20 resize-y"
              aria-label="Approval note"
            />
          ) : null}
          {wrapPrimary(
            <LoadingButton
              type="button"
              className="w-full"
              disabled={primaryDisabled}
              loading={primaryPending}
              loadingText="Approving..."
              onClick={onPrimaryAction}
            >
              {primaryLabel}
            </LoadingButton>,
          )}
          {primaryHint ? (
            <p className="text-xs text-muted-foreground">{primaryHint}</p>
          ) : null}
          {footer}
        </div>
      </div>
    </aside>
  );
}
